import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";

const initialState = {
	categories: [],
	category: null,
	subcategories: [],
	isLoading: false,
	error: null,
};

// 1. Create Main Category (Protected)
export const createCategory = createAsyncThunk("lmsCategory/createCategory", async ({ name, is_active }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post(`lms/categories`, { name, is_active });
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// 2. Get All Categories (Public)
export const fetchCategories = createAsyncThunk("lmsCategory/fetchCategories", async ({ page, limit, filters } = {}, { rejectWithValue }) => {
	try {
		const queryParams = new URLSearchParams({
			page: page ?? 1,
			limit: limit ?? 100,
			search: filters?.search ?? "",
			activeOnly: filters?.activeOnly ? "true" : "",
		});

		const response = await axiosHttp.get(`lms/categories?${queryParams}`);
		return response.data.data.categories || [];
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// 2b. Get Active Categories Only (For Dropdowns)
export const fetchActiveCategories = createAsyncThunk("lmsCategory/fetchActiveCategories", async (_, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`lms/categories/active`);
		return response.data.data || [];
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// 3. Get Category by ID (Public)
export const fetchCategoryById = createAsyncThunk("lmsCategory/fetchCategoryById", async ({ categoryId }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`lms/categories/${categoryId}`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// 4. Update Category (Protected)
export const updateCategory = createAsyncThunk("lmsCategory/updateCategory", async ({ categoryId, name, is_active }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.put(`lms/categories/${categoryId}`, { name, is_active });
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// 5. Toggle Category Status (Protected)
export const toggleCategoryStatus = createAsyncThunk("lmsCategory/toggleCategoryStatus", async ({ categoryId }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.patch(`lms/categories/${categoryId}/toggle`, {});
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// 6. Create Subcategory (Protected)
export const createSubcategory = createAsyncThunk("lmsCategory/createSubcategory", async ({ categoryId, name, is_active }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post(`lms/categories/${categoryId}/subcategories`, { name, is_active });
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// 7. Get Subcategories by Category (Public)
export const fetchSubcategories = createAsyncThunk("lmsCategory/fetchSubcategories", async ({ categoryId, filters }, { rejectWithValue }) => {
	try {
		const params = new URLSearchParams({
			category_id: categoryId.toString(),
			activeOnly: filters?.activeOnly ? "true" : "",
		});
		const response = await axiosHttp.get(`lms/categories/${categoryId}/subcategories`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// 8. Update Subcategory (Protected)
export const updateSubcategory = createAsyncThunk(
	"lmsCategory/updateSubcategory",
	async ({ subcategoryId, name, is_active }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.put(`lms/categories/subcategories/${subcategoryId}`, { name, is_active });
			return response.data.data;
		} catch (error) {
			return rejectWithValue(error.response?.data || error.message);
		}
	},
);

// 9. Toggle Subcategory Status (Protected)
export const toggleSubcategoryStatus = createAsyncThunk("lmsCategory/toggleSubcategoryStatus", async ({ subcategoryId }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.patch(`lms/categories/subcategories/${subcategoryId}/toggle`, {});
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

// Delete a category (additional utility)
export const deleteCategory = createAsyncThunk("lmsCategory/deleteCategory", async ({ categoryId }, { rejectWithValue }) => {
	try {
		await axiosHttp.delete(`lms/categories/${categoryId}`);
		return categoryId;
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

const categorySlice = createSlice({
	name: "lmsCategory",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearSubcategories: (state) => {
			state.subcategories = [];
		},
	},
	extraReducers: (builder) => {
		builder
			// Create Category
			.addCase(createCategory.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(createCategory.fulfilled, (state, action) => {
				state.isLoading = false;
			})
			.addCase(createCategory.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch All Categories
			.addCase(fetchCategories.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchCategories.fulfilled, (state, action) => {
				state.isLoading = false;
				state.categories = action.payload;
			})
			.addCase(fetchCategories.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch Active Categories
			.addCase(fetchActiveCategories.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchActiveCategories.fulfilled, (state, action) => {
				state.isLoading = false;
				state.categories = action.payload;
			})
			.addCase(fetchActiveCategories.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch Category by ID
			.addCase(fetchCategoryById.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchCategoryById.fulfilled, (state, action) => {
				state.isLoading = false;
				state.category = action.payload;
			})
			.addCase(fetchCategoryById.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Update Category
			.addCase(updateCategory.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(updateCategory.fulfilled, (state, action) => {
				state.isLoading = false;
			})
			.addCase(updateCategory.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Toggle Category Status
			.addCase(toggleCategoryStatus.pending, (state) => {
				state.error = null;
			})
			.addCase(toggleCategoryStatus.fulfilled, (state, action) => {
				state.error = null;
			})
			.addCase(toggleCategoryStatus.rejected, (state, action) => {
				state.error = action.payload;
			})

			// Create Subcategory
			.addCase(createSubcategory.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(createSubcategory.fulfilled, (state, action) => {
				state.isLoading = false;
			})
			.addCase(createSubcategory.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch Subcategories
			.addCase(fetchSubcategories.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchSubcategories.fulfilled, (state, action) => {
				state.isLoading = false;
				state.subcategories = action.payload;
			})
			.addCase(fetchSubcategories.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Update Subcategory
			.addCase(updateSubcategory.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(updateSubcategory.fulfilled, (state, action) => {
				state.isLoading = false;
			})
			.addCase(updateSubcategory.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Toggle Subcategory Status
			.addCase(toggleSubcategoryStatus.pending, (state) => {
				state.error = null;
			})
			.addCase(toggleSubcategoryStatus.fulfilled, (state, action) => {
				state.error = null;
			})
			.addCase(toggleSubcategoryStatus.rejected, (state, action) => {
				state.error = action.payload;
			})

			// Delete Category
			.addCase(deleteCategory.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(deleteCategory.fulfilled, (state, action) => {
				state.isLoading = false;
			})
			.addCase(deleteCategory.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			});
	},
});

export const { clearError, clearSubcategories } = categorySlice.actions;
export default categorySlice.reducer;
